"use client";

import { Typography, Paper, Button, CircularProgress } from "@mui/material";
// Image - use regular img tag
import ReplayIcon from "@mui/icons-material/Replay";
import { Lock } from "@mui/icons-material";
import {
  useResendPasswordMutation,
  useVerifyEmailMutation,
} from "@/store/services/auth";
import { toast } from "react-toastify";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import FuseLoading from "@fuse/core/FuseLoading";

function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getRawQueryParam = (key: string): string | null => {
    if (typeof window === "undefined") return null;

    const match = window.location.search.match(
      new RegExp("[?&]" + key + "=([^&]*)")
    );
    return match ? match[1] : null;
  };

  const rawEmailParam = getRawQueryParam("email");
  const email = rawEmailParam
    ? decodeURIComponent(rawEmailParam)
    : searchParams.get("email") ?? "";
  const token = searchParams.get("token");
  const [isVerified, setIsVerified] = useState(false);
  const [resendPassword, { isLoading: isLoadingResend }] =
    useResendPasswordMutation();
  const [verifyEmail, { isLoading: isLoadingVerify }] =
    useVerifyEmailMutation();

  const handleResend = async () => {
    try {
      const result: any = await resendPassword({ email });

      if (result.error) {
        toast.error(
          result.error?.data?.errors[0]?.detail || "Something went wrong!"
        );
      } else {
        // toast.success("Verification Email Sent!");
        toast.success(
          result?.data?.message ||
            "Approval request resent to admin successfully."
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleVerify = async () => {
    try {
      const result: any = await verifyEmail({ email, token });

      if (result.error) {
        toast.error(
          result.error?.data?.errors[0]?.detail || "Something went wrong!"
        );
        setIsVerified(false);
      } else {
        toast.success("Email Verified!");
        setIsVerified(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogin = () => {
    navigate("/sign-in");
  };

  useEffect(() => {
    if (email && token) {
      handleVerify();
    }
  }, [email, token]);

  if (isLoadingVerify) {
    return <FuseLoading />;
  }

  return (
    <div className="flex min-w-0 flex-auto flex-col items-center sm:justify-center">
      <Paper className="bg-white min-h-full w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:px-12 sm:py-10 sm:shadow-sm">
        <div className="mx-auto w-full max-w-80 sm:mx-0 sm:w-80 flex flex-col items-center">
          <img
            src="/assets/images/logo/logo_loanapp.png"
            alt="ASSETS ID"
            width={165}
            height={70}
          />

          <div className="w-full flex flex-col items-center">
            <Typography className="mt-8 text-4xl font-extrabold leading-[1.25] tracking-tight mb-5">
              Verify Email
            </Typography>
            <div className="mt-0.5 flex items-baseline font-medium">
              <Typography className="text-center">
                {isVerified ? (
                  <>
                    Your email address <strong>{email}</strong> has been
                    successfully verified. Click the button below to log in.
                  </>
                ) : (
                  <>
                    Email verification for <strong>{email}</strong> hasn’t been
                    completed yet. If you didn’t receive the email or the link
                    expired, click the button below to resend a new verification
                    link.
                  </>
                )}
              </Typography>
            </div>
            <div className="flex gap-3 mt-8">
              {!isVerified ? (
                <Button
                  variant="contained"
                  className=" mt-1 w-full"
                  aria-label="Register"
                  type="submit"
                  size="large"
                  color="secondary"
                  onClick={handleResend}
                  disabled={isLoadingResend}
                  startIcon={
                    isLoadingResend ? (
                      <CircularProgress size={20} />
                    ) : (
                      <ReplayIcon />
                    )
                  }
                >
                  Resend Link
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  className=" mt-1 w-full"
                  aria-label="Register"
                  type="submit"
                  size="large"
                  startIcon={<Lock />}
                  onClick={handleLogin}
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
}

export default VerifyEmailPage;
